import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Derives WhatsApp campaign lifecycle UI state from campaign and execution statuses.
 *
 * @param {import('vue').MaybeRefOrGetter<string>} campaignStatus - campaign.campaign_status
 * @param {import('vue').MaybeRefOrGetter<string>} executionStatus - latest execution display status
 */
export function useWhatsAppCampaignStatus(campaignStatus, executionStatus) {
  const { t } = useI18n();

  const resolvedCampaignStatus = computed(() => toValue(campaignStatus) || '');
  const resolvedExecutionStatus = computed(
    () => toValue(executionStatus) || ''
  );

  const isStopped = computed(() => resolvedCampaignStatus.value === 'stopped');
  const isCompleted = computed(
    () => resolvedCampaignStatus.value === 'completed'
  );
  const isPaused = computed(
    () =>
      resolvedCampaignStatus.value === 'paused' ||
      resolvedExecutionStatus.value === 'paused'
  );
  const isSending = computed(
    () =>
      resolvedCampaignStatus.value === 'active' &&
      resolvedExecutionStatus.value === 'running'
  );
  const isScheduled = computed(
    () =>
      resolvedCampaignStatus.value === 'active' &&
      resolvedExecutionStatus.value !== 'running' &&
      resolvedExecutionStatus.value !== 'paused'
  );

  const statusLabel = computed(() => {
    if (isPaused.value) return t('CAMPAIGN.LIST.STATUS.PAUSED');
    if (isStopped.value) return t('CAMPAIGN.LIST.STATUS.STOPPED');
    if (isCompleted.value) return t('CAMPAIGN.LIST.STATUS.COMPLETED');
    if (isSending.value) return t('CAMPAIGN.LIST.STATUS.SENDING');
    return t('CAMPAIGN.LIST.STATUS.SCHEDULED');
  });

  const statusBadgeClass = computed(() => {
    if (isPaused.value) return 'bg-n-alpha-2 text-n-yellow-11';
    if (isStopped.value) return 'bg-n-alpha-2 text-n-ruby-11';
    if (isSending.value) return 'bg-n-alpha-2 text-n-teal-11';
    if (isCompleted.value) return 'bg-n-alpha-2 text-n-slate-12';
    if (isScheduled.value) return 'bg-n-alpha-2 text-n-slate-11';
    return 'bg-n-alpha-2 text-n-slate-12';
  });

  const canPause = computed(() => isSending.value);
  const canResume = computed(() => isPaused.value);
  const canStop = computed(
    () =>
      (isSending.value || isPaused.value) &&
      !isCompleted.value &&
      !isStopped.value
  );
  const canCancel = computed(() => isScheduled.value);
  const shouldPoll = computed(() => isSending.value || isPaused.value);

  return {
    isScheduled,
    isSending,
    isPaused,
    isStopped,
    isCompleted,
    statusLabel,
    statusBadgeClass,
    canPause,
    canResume,
    canStop,
    canCancel,
    shouldPoll,
  };
}
